import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import {
	T,
	always,
	cond,
	equals,
	filter,
	find,
	head,
	length,
	map,
	propEq,
	reduce,
	slice,
} from 'ramda';
import React, { FC } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FlexRow, { IFlexRow } from '@ui/atoms/FlexRow';
import SubheadingText from '@ui/atoms/SubheadingText';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { ILegalName, MemberInfo, SubscriptionInfo } from '@hooks/useMemberInfo';

import { interpolate } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';

import { FolxMembershipStatus } from '@data/membership';

import { IActiveSubscriptionRx } from '@folx-types/medications';

import theme from '@theme/FolxTheme';

export interface IPlanCardWrapperContent {
	planHeader: string;
	refillDescription: string;
	canceledDescription: string;
	pastDueDescription: string;
	reactivateLabel: string;
	updateLabel: string;
	estrogenInjectableImage: string;
	estrogenInjectableAlt: string;
	estrogenSublingualImage: string;
	estrogenSublingualAlt: string;
	estrogenPatchImage: string;
	estrogenPatchAlt: string;
	testosteroneInjectableImage: string;
	testosteroneInjectableAlt: string;
	testosteroneGelImage: string;
	testosteroneGelAlt: string;
	prepSublingualImage: string;
	prepSublingualAlt: string;
}

export interface IPlanCardProps {
	activePrescriptions: IActiveSubscriptionRx[];
	wrapperContent: IPlanCardWrapperContent;
	member?: MemberInfo & SubscriptionInfo & ILegalName;
}

const AlignedRow = aligned<IFlexRow>(FlexRow);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);

const GridCard = styled(BaseCard)`
	grid-column-start: span 2;
	grid-column-end: auto;
	padding: ${theme.spacing.sm} ${theme.spacing.md};
	margin-bottom: ${theme.spacing.xssm};

	@media ${theme.responsive.breakpoints.tablet} {
		grid-column-start: span 2;
		padding: ${theme.spacing.md} ${theme.spacing.lg};
		margin-bottom: 0;
	}
`;

const PlanImage = styled.img`
	height: 5rem;
	width: 7.5rem;
	border-radius: 1.09rem;
	object-fit: cover;
`;

const PlanCard: FC<IPlanCardProps> = ({ activePrescriptions, member, wrapperContent }) => {
	const {
		planHeader,
		refillDescription,
		canceledDescription,
		pastDueDescription,
		reactivateLabel,
		updateLabel,
		estrogenInjectableImage,
		estrogenInjectableAlt,
		estrogenSublingualImage,
		estrogenSublingualAlt,
		estrogenPatchImage,
		estrogenPatchAlt,
		testosteroneInjectableImage,
		testosteroneInjectableAlt,
		testosteroneGelImage,
		testosteroneGelAlt,
		prepSublingualImage,
		prepSublingualAlt,
	} = wrapperContent;

	const memberStatus = member?.membershipSubscription?.status;
	const isCanceledMember = memberStatus === FolxMembershipStatus.CANCELED;
	const isPastDueMember = memberStatus === FolxMembershipStatus.PAST_DUE;

	const getPrepMed = find(propEq('medClass', 'PREP'));

	const getMedicationImage = () => {
		const applicableMed = head(
			filter((prescription: IActiveSubscriptionRx) => {
				const subType = prescription.medSubscriptionType;
				return subType === 'HRT_T' || subType === 'HRT_E';
			})(activePrescriptions)
		);
		const medClass = applicableMed?.medClass ?? getPrepMed(activePrescriptions)?.medClass ?? '';

		return cond([
			[equals('E_INJ'), always({ image: estrogenInjectableImage, alt: estrogenInjectableAlt })],
			[equals('E_ORAL'), always({ image: estrogenSublingualImage, alt: estrogenSublingualAlt })],
			[equals('E_PATCH'), always({ image: estrogenPatchImage, alt: estrogenPatchAlt })],
			[
				equals('T_INJ'),
				always({ image: testosteroneInjectableImage, alt: testosteroneInjectableAlt }),
			],
			[equals('T_GEL'), always({ image: testosteroneGelImage, alt: testosteroneGelAlt })],
			[equals('PREP'), always({ image: prepSublingualImage, alt: prepSublingualAlt })],
			[T, always(null)],
		])(medClass);
	};

	const getMedicationNames = map((prescription: IActiveSubscriptionRx) => {
		const medName = prescription.medName;
		const prescriptionName = `
				${slice(0, 1, medName).toUpperCase()}${slice(1, length(medName), medName)}
			`;
		return <BasicText variant="fineprint">{prescriptionName}</BasicText>;
	}, activePrescriptions);

	const getRefillDescription = () => {
		let hasFutureRefills = false;

		const refillDate: DateTime = reduce(
			(acc: string | DateTime, prescription: IActiveSubscriptionRx) => {
				const accumulatedDate: DateTime = DateTime.isDateTime(acc)
					? (acc as DateTime)
					: DateTime.fromISO(acc as string);

				if (doesNotExist(prescription.dateRefillEligible)) {
					return accumulatedDate;
				}

				const refillEligibleDate = DateTime.fromISO(prescription.dateRefillEligible);

				if (!refillEligibleDate.isValid) {
					return accumulatedDate;
				}
				if (refillEligibleDate > DateTime.now()) {
					hasFutureRefills = true;
				}

				if (!accumulatedDate.isValid) {
					return refillEligibleDate;
				}
				if (refillEligibleDate < DateTime.now()) {
					return hasFutureRefills
						? accumulatedDate
						: accumulatedDate > refillEligibleDate
						? accumulatedDate
						: refillEligibleDate;
				}

				return refillEligibleDate < accumulatedDate ? refillEligibleDate : accumulatedDate;
			},
			'',
			activePrescriptions
		);

		return interpolate(refillDescription, {
			refillDate: refillDate?.isValid ? refillDate.toLocaleString(DateTime.DATE_FULL) : 'N/A',
		});
	};

	return exists(activePrescriptions) ? (
		<GridCard borderless bgColor="white" radius="xl">
			<FullWidthColumn gap="sm">
				<SubheadingText variant="bold">{planHeader}</SubheadingText>
				{exists(activePrescriptions) && (
					<AlignedRow alignItems="center" gap="sm">
						{exists(getMedicationImage()) && (
							<PlanImage
								src={getMedicationImage()?.image ?? ''}
								alt={getMedicationImage()?.alt ?? ''}
							/>
						)}
						<FlexColumn>{getMedicationNames}</FlexColumn>
					</AlignedRow>
				)}
				<BasicText variant="fineprint">
					{isCanceledMember
						? canceledDescription
						: isPastDueMember
						? pastDueDescription
						: getRefillDescription()}
				</BasicText>
				{isCanceledMember && (
					<Button
						radius="xxl"
						label={reactivateLabel}
						onClick={() => navigate('./reactivate_membership/summary')}
					/>
				)}
				{isPastDueMember && (
					<Button radius="xxl" label={updateLabel} onClick={() => navigate('./billing/bills')} />
				)}
			</FullWidthColumn>
		</GridCard>
	) : (
		<></>
	);
};

export default styled(PlanCard)``;
